'use client';

import { useStudioCourse } from '@/api/queries/studio-courses.queries';
import { CourseStatusPill } from '@/app/studio/courses/_components/CourseStatusPill';
import { BarChart2, MessageSquare, Paperclip, PlaySquare, Settings } from 'lucide-react';
import { ContextualSidebarShell } from './ContextualSidebarShell';
import type { ContextualNavItem, ContextualSidebarComponentProps } from './types';

/**
 * Course-scoped sidebar content. Mounted by StudioSidebar when pathname
 * matches `/studio/courses/<uuid>/`. Shares the course query cache with
 * `CourseDetailShell` via TanStack — no extra network call.
 */
export function CourseSidebarContent({ paramValue: uuid }: ContextualSidebarComponentProps) {
  const { data: course } = useStudioCourse(uuid);

  const navItems: ContextualNavItem[] = [
    {
      id: 'details',
      label: 'Details',
      href: `/studio/courses/${uuid}/details`,
      icon: Settings,
    },
    {
      id: 'videos',
      label: 'Lessons',
      href: `/studio/courses/${uuid}/videos`,
      icon: PlaySquare,
    },
    {
      id: 'resources',
      label: 'Resources',
      href: `/studio/courses/${uuid}/resources`,
      icon: Paperclip,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      href: `/studio/courses/${uuid}/details`,
      icon: BarChart2,
      disabled: true,
      badge: 'Soon',
    },
    {
      id: 'comments',
      label: 'Comments',
      href: `/studio/courses/${uuid}/details`,
      icon: MessageSquare,
      disabled: true,
      badge: 'Soon',
    },
  ];

  const lessonsLabel =
    course === undefined
      ? null
      : `${course.videos_count} ${course.videos_count === 1 ? 'lesson' : 'lessons'}`;

  return (
    <ContextualSidebarShell
      backHref="/studio/courses"
      backLabel="Channel content"
      entity={{
        thumbnail: course?.thumbnail,
        title: course?.title,
        eyebrow: 'Your course',
        statusPill: course ? <CourseStatusPill status={course.status} /> : null,
        metaLine: lessonsLabel,
      }}
      navItems={navItems}
    />
  );
}
